import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { isBookingDomain, getAppBranding } from '../config/portalMode';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const bookingMode = isBookingDomain();
  const branding = getAppBranding();
  const [showClientLogin, setShowClientLogin] = useState(bookingMode);
  const [clientEmail, setClientEmail] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  useEffect(() => {
    // Check for error from callback
    const errorParam = searchParams.get('error');
    const emailParam = searchParams.get('email');

    if (errorParam) {
      switch (errorParam) {
        case 'user_not_found':
          setError(
            `Account not found${emailParam ? ` for ${emailParam}` : ''}. Please contact your administrator to create your account.`
          );
          break;
        case 'account_inactive':
          setError('Your account has been deactivated. Please contact your administrator.');
          break;
        case 'token_failed':
          setError('Authentication failed. Please try again.');
          break;
        case 'server_error':
          setError('Server error occurred. Please try again later.');
          break;
        default:
          setError('An error occurred during login. Please try again.');
      }
    }
  }, [searchParams]);

  const handleClientLogin = async (e) => {
    e.preventDefault();
    if (!clientEmail.trim() || !clientPassword) {
      setError('Please enter your email and password.');
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/auth/client-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clientEmail.trim(), password: clientPassword }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Login failed.');
        setIsLoading(false);
        return;
      }
      if (data.requiresMfa && data.mfaToken) {
        setMfaToken(data.mfaToken);
        setMfaCode('');
        setError(null);
        setIsLoading(false);
        return;
      }
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      navigate('/', { replace: true });
      window.location.reload();
    } catch (err) {
      console.error('Client login error:', err);
      setError('Could not reach the server. Please try again.');
      setIsLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (!mfaToken || !mfaCode.trim()) {
      setError('Please enter the 6-digit code from your authenticator app.');
      return;
    }
    try {
      setMfaLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/auth/mfa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken, code: mfaCode.trim() }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Invalid code.');
        setMfaLoading(false);
        return;
      }
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      navigate('/', { replace: true });
      window.location.reload();
    } catch (err) {
      console.error('MFA verify error:', err);
      setError('Verification failed. Please try again.');
      setMfaLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get the Microsoft auth URL from backend
      const response = await fetch(`${API_URL}/auth/microsoft`);
      const data = await response.json();

      if (!response.ok) {
        // API returned an error
        setError(data.message || data.error || 'Failed to initialize login. Please check server configuration.');
        setIsLoading(false);
        return;
      }

      if (data.authUrl) {
        // Redirect to Microsoft login
        window.location.href = data.authUrl;
      } else {
        setError('Failed to initialize login - no auth URL received from server');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(`Failed to connect to server: ${err.message}. Please ensure the API server is running.`);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo/Title */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Logo size={64} />
              <h1 className="text-2xl font-bold text-blue-600">{branding.name}</h1>
            </div>
            <p className="text-gray-600">{branding.subtitle}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Tabs: Company vs Client portal — hidden on booking domain */}
          {!bookingMode && (
            <div className="flex rounded-lg border border-gray-200 p-1 mb-6 bg-gray-50">
              <button
                type="button"
                onClick={() => { setShowClientLogin(false); setError(null); }}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${!showClientLogin ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Company account
              </button>
              <button
                type="button"
                onClick={() => { setShowClientLogin(true); setError(null); }}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${showClientLogin ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Client portal
              </button>
            </div>
          )}

          {!showClientLogin ? (
            <>
              {/* Microsoft Login Button */}
              <button
                onClick={handleMicrosoftLogin}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-300 rounded-lg shadow-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 23 23">
                  <path fill="#f35325" d="M0 0h11v11H0z" />
                  <path fill="#81bc06" d="M12 0h11v11H12z" />
                  <path fill="#05a6f0" d="M0 12h11v11H0z" />
                  <path fill="#ffba08" d="M12 12h11v11H12z" />
                </svg>
                <span className="text-gray-700 font-medium">
                  {isLoading ? 'Connecting...' : 'Sign in with Microsoft 365'}
                </span>
              </button>
              <div className="mt-4 text-center text-sm text-gray-600">
                <p>Use your company email to sign in</p>
              </div>
            </>
          ) : mfaToken ? (
            <>
              {/* MFA code step */}
              <p className="text-sm text-gray-600 mb-4">Enter the 6-digit code from your authenticator app.</p>
              <form onSubmit={handleMfaVerify} className="space-y-4">
                <div>
                  <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
                  <input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest"
                    placeholder="000000"
                  />
                </div>
                <button
                  type="submit"
                  disabled={mfaLoading || mfaCode.length < 6}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-transparent rounded-lg shadow-sm bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {mfaLoading ? 'Verifying...' : 'Verify and sign in'}
                </button>
                <button
                  type="button"
                  onClick={() => { setMfaToken(null); setMfaCode(''); setError(null); }}
                  className="w-full text-sm text-gray-600 hover:text-gray-900"
                >
                  Back to sign in
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Client portal email + password */}
              <form onSubmit={handleClientLogin} className="space-y-4">
                <div>
                  <label htmlFor="client-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    id="client-email"
                    type="email"
                    required
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="you@yourcompany.com"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label htmlFor="client-password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    id="client-password"
                    type="password"
                    required
                    value={clientPassword}
                    onChange={(e) => setClientPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-transparent rounded-lg shadow-sm bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Signing in...' : 'Sign in to client portal'}
                </button>
              </form>
              <div className="mt-4 text-center text-sm text-gray-600">
                <p>For client users: sign in with the email and password set by your administrator.</p>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center text-xs text-gray-500">
            <p>{bookingMode ? 'Sign in with your portal credentials' : (showClientLogin ? 'Client portal: email and password' : 'Company sign-in: Microsoft 365')}</p>
          </div>
        </div>

        {/* Support Info */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>
            Need access?{' '}
            <a href="mailto:support@example.com" className="text-blue-600 hover:underline">
              Contact your administrator
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

